// VAULT DROPS on a band page — each released live set: posters (whole), the tap-anywhere waveform, kept photos (foldable).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Disc3 } from "lucide-react";
import { API } from "../../App";
import { auth, abs } from "../voltbox/vbShared";
import { EpisodeWave } from "../voltbox/EpisodeWave";
import { PosterFrame } from "../voltbox/PosterFrame";
import { FoldablePhotos } from "../voltbox/FoldablePhotos";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" }) : "");

const Drop = ({ s, isAdmin }) => {
  const fold = (collapsed) => axios.put(`${API}/voltbox/${s.item_id}/photos-collapsed`, { collapsed }, auth());
  const hasPosters = s.posters?.length > 0;
  const wave = s.episode?.audio_url && <EpisodeWave ep={s.episode} slug={s.episode.slug} />;
  return (
    <article className="rounded-3xl bg-[#080E14] text-white ring-1 ring-[#54E3FF]/30 p-5 sm:p-7 shadow-xl" data-testid={`band-vault-drop-${s.item_id}`}>
      <div className={hasPosters ? "grid lg:grid-cols-[minmax(0,1fr)_minmax(0,26rem)] gap-6 items-start" : ""}>
        <div className="min-w-0">
          <p className="m-0 text-[10px] font-black tracking-[0.3em] text-[#F2CC8F]">VAULT DROP{s.published_at ? ` · ${when(s.published_at).toUpperCase()}` : ""}</p>
          <h3 className="m-0 mt-2 font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold leading-tight">
            <Link to={`/news/${s.episode.slug}`} className="text-white no-underline hover:text-[#54E3FF]" data-testid={`band-vault-drop-link-${s.item_id}`}>{s.title}</Link>
          </h3>
          {s.description && <p className="m-0 mt-3 text-sm text-white/70 leading-relaxed whitespace-pre-line max-w-2xl">{s.description}</p>}
          {hasPosters && wave}
        </div>
        {hasPosters && <PosterFrame posters={s.posters} maxH="18rem" testid={`band-vault-posters-${s.item_id}`} />}
      </div>
      {!hasPosters && wave}
      {s.photos?.length > 0 && <div className="mt-6"><FoldablePhotos photos={s.photos} columns={s.gallery_columns} startFolded={s.photos_collapsed} onPersist={isAdmin ? fold : undefined} testid={`band-vault-photos-${s.item_id}`} /></div>}
    </article>
  );
};

const ComingSoon = ({ s }) => {
  const img = s.posters?.[0]?.url || s.still_url;
  return (
    <article className="rounded-3xl bg-[#080E14] text-white ring-1 ring-[#F2CC8F]/40 p-5 sm:p-7 shadow-xl overflow-hidden" data-testid={`band-vault-soon-${s.item_id}`}>
      <div className={img ? "grid md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] gap-6 items-center" : ""}>
        <div className="min-w-0">
          <p className="m-0 inline-flex items-center gap-2 text-[10px] font-black tracking-[0.3em] text-[#F2CC8F]"><span className="h-2 w-2 rounded-full bg-[#F2CC8F] animate-pulse" /> COMING SOON · VAULT DROP</p>
          <h3 className="m-0 mt-2 font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold leading-tight" data-testid={`band-vault-soon-title-${s.item_id}`}>{s.title}</h3>
          {s.description && <p className="m-0 mt-3 text-sm text-white/70 leading-relaxed whitespace-pre-line max-w-2xl">{s.description}</p>}
          <p className="m-0 mt-4 text-xs font-bold uppercase tracking-[0.2em] text-white/45">{s.songs ? `${s.songs} songs · ` : ""}recorded live · in the edit now</p>
        </div>
        {img && <img src={abs(img)} alt={`${s.title} — poster`} loading="lazy" className="w-full max-h-80 object-contain rounded-2xl bg-black/40" data-testid={`band-vault-soon-img-${s.item_id}`} />}
      </div>
    </article>
  );
};

export const VaultDropsShelf = ({ slug, name, isAdmin }) => {
  const [sets, setSets] = useState([]);
  useEffect(() => { axios.get(`${API}/bands/${slug}/vault-sets`).then((r) => setSets(r.data || [])).catch(() => setSets([])); }, [slug]);
  if (!sets.length) return null;
  const allSoon = sets.every((s) => s.coming_soon);
  return (
    <section className="mb-10" data-testid="band-vault-drops">
      <div className="flex items-center gap-3 mb-4">
        <Disc3 className="h-6 w-6 text-[#54E3FF]" />
        <div>
          <span className="block text-[10px] font-bold uppercase tracking-[0.25em] text-[#54E3FF]">VAULT DROPS · live from THE WALL</span>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-semibold text-[#1A1A1A] leading-tight m-0">{allSoon ? `${name} live — coming soon` : `Hear ${name} live — tap anywhere on the wave`}</h2>
        </div>
      </div>
      <div className="flex flex-col gap-6">{sets.map((s) => (s.coming_soon ? <ComingSoon key={s.item_id} s={s} /> : <Drop key={s.item_id} s={s} isAdmin={isAdmin} />))}</div>
    </section>
  );
};
